import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { LeftMenuComponent } from '../../../menu/left-menu/left-menu.component';
import { AiAssessmentAdminService } from '../../../services/ai-assessment-admin.service';
import { AssessmentJob, AssessmentJobStatus, AssessmentJobType } from '../../../models/chat.models';
import { formatUtcDate } from '../../../shared/date-utils';

@Component({
  selector: 'app-ai-assessment-job-detail',
  templateUrl: './ai-assessment-job-detail.component.html',
  styleUrls: ['./ai-assessment-job-detail.component.scss'],
  imports: [CommonModule, MaterialModule, SpinnerComponent, LeftMenuComponent]
})
export class AiAssessmentJobDetailComponent implements OnInit, OnDestroy {
  jobId = 0;
  job: AssessmentJob | null = null;
  loading = true;
  error: string | null = null;
  returnTo: 'ai-config' | 'ai-assessment' = 'ai-assessment';

  // Live logs for running jobs
  logLines: string[] = [];
  lastLogId = 0;
  private logSubscription?: Subscription;
  private refreshSubscription?: Subscription;

  AssessmentJobStatus = AssessmentJobStatus;
  formatDate = formatUtcDate;

  @ViewChild('logConsoleEl') logConsoleEl?: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private assessmentAdminService: AiAssessmentAdminService
  ) {}

  ngOnInit(): void {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    if (returnTo === 'ai-config') {
      this.returnTo = 'ai-config';
    }

    this.route.params.subscribe(params => {
      this.jobId = +params['id'];
      if (this.jobId) {
        this.loadJob(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.logSubscription?.unsubscribe();
    this.refreshSubscription?.unsubscribe();
  }

  loadJob(isInitial = false): void {
    this.assessmentAdminService.getJobById(this.jobId).subscribe({
      next: (job) => {
        this.job = job;
        if (isInitial) {
          this.loading = false;
          this.startLiveLogsIfRunning();
        }
        // Refresh job status while running
        if (this.isRunning(job)) {
          this.scheduleRefresh();
        } else {
          this.refreshSubscription?.unsubscribe();
        }
      },
      error: (err) => {
        console.error('Error loading assessment job:', err);
        this.error = `Failed to load job ${this.jobId}.`;
        this.loading = false;
      }
    });
  }

  private scheduleRefresh(): void {
    if (!this.refreshSubscription) {
      this.refreshSubscription = interval(5000).subscribe(() => this.loadJob());
    }
  }

  private startLiveLogsIfRunning(): void {
    if (!this.job) return;
    // Stream logs if job is running or just completed
    this.logSubscription = interval(4000)
      .pipe(
        startWith(0),
        switchMap(() => this.assessmentAdminService.getLiveLogs(this.lastLogId))
      )
      .subscribe({
        next: (response) => {
          if (response.entries?.length) {
            const newLines = response.entries.map(e => this.formatLogLine(e));
            this.logLines = [...this.logLines, ...newLines];
            this.lastLogId = response.latestId;
            setTimeout(() => this.scrollLogsToBottom(), 50);
          } else if (response.latestId) {
            this.lastLogId = response.latestId;
          }
        },
        error: () => { /* best-effort */ }
      });
  }

  goBack(): void {
    if (this.returnTo === 'ai-config') {
      this.router.navigate(['/admin/ai-config'], { queryParams: { tab: 'history' } });
      return;
    }

    this.router.navigate(['/admin/ai-config'], { queryParams: { tab: 'history' } });
  }

  isRunning(job: AssessmentJob | null): boolean {
    return job?.status === AssessmentJobStatus.Running || job?.status === 'Running';
  }

  isPending(job: AssessmentJob | null): boolean {
    return job?.status === AssessmentJobStatus.Pending || job?.status === 'Pending';
  }

  isRateLimited(job: AssessmentJob | null): boolean {
    return this.isRunning(job)
      && !!job?.rateLimitedUntil
      && new Date(job.rateLimitedUntil).getTime() > Date.now();
  }

  formatRateLimitedUntil(job: AssessmentJob): string {
    return job.rateLimitedUntil
      ? formatUtcDate(new Date(job.rateLimitedUntil), 'short')
      : '—';
  }

  getStatusClass(status: AssessmentJobStatus | string): string {
    switch (status) {
      case AssessmentJobStatus.Completed:
      case 'Completed': return 'badge-success';
      case AssessmentJobStatus.Running:
      case 'Running': return 'badge-primary';
      case AssessmentJobStatus.Failed:
      case 'Failed': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  getStatusLabel(status: AssessmentJobStatus | string): string {
    if (typeof status === 'string') return status;
    return AssessmentJobStatus[status] ?? String(status);
  }

  isLightweightJob(job: AssessmentJob | null): boolean {
    return job?.jobType === AssessmentJobType.LightweightCodeValidation
      || job?.jobType === 'LightweightCodeValidation'
      || job?.jobType === '1'
      || (job?.jobType as any) === 1;
  }

  getJobTypeLabel(job: AssessmentJob | null): string {
    return this.isLightweightJob(job) ? 'Lightweight Code Validation' : 'Full Code Assessment';
  }

  getDuration(job: AssessmentJob): string {
    if (!job.startedAt || !job.completedAt) return '—';
    const ms = new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime();
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  }

  private formatLogLine(entry: any): string {
    const ts = new Date(entry.timestamp).toLocaleTimeString();
    return `[${ts}] [${entry.level}] [${entry.category}] ${entry.message}`;
  }

  private scrollLogsToBottom(): void {
    if (this.logConsoleEl) {
      const el = this.logConsoleEl.nativeElement as HTMLElement;
      el.scrollTop = el.scrollHeight;
    }
  }
}
